import { type ReactNode, useEffect, useState } from 'react';
import { useExperiment } from '@hungryroot/experiments';
import { HamburgerMenuUtility } from '@hungryroot/icons';
import { TransparentButton } from '@hungryroot/ramen';
import { buttonClickedEvent } from '@hungryroot/segment';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import { MobileMenu } from '../MobileMenu/MobileMenu';
import styles from './CatalogNavigation.module.css';

export function CatalogNavigation({
  className,
  desktopNavLinks,
  mobileNavLinks,
  requireExpEnrollment = true,
}: {
  className?: string;
  desktopNavLinks: ReactNode;
  mobileNavLinks: ReactNode;
  requireExpEnrollment?: boolean;
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const preAuthCatalogVariant = useExperiment(199);

  useEffect(() => {
    const mediaQuery = window.matchMedia(BreakpointQueryLarge);

    function handleResize(event: MediaQueryListEvent) {
      if (event.matches) {
        // eslint-disable-next-line react-you-might-not-need-an-effect/you-might-not-need-an-effect
        setIsMobileMenuOpen(false);
      }
    }

    mediaQuery.addEventListener('change', handleResize);

    return () => {
      mediaQuery.removeEventListener('change', handleResize);
    };
  }, []);

  // Close menu when URL path changes (SPA navigation)
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handleNavigation = () => {
      setIsMobileMenuOpen(false);
    };

    // Listen to popstate for back/forward navigation
    window.addEventListener('popstate', handleNavigation);

    // Patch history methods to detect client-side navigation (React Router)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(this, args);
      handleNavigation();
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(this, args);
      handleNavigation();
    };

    return () => {
      window.removeEventListener('popstate', handleNavigation);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const onExitIntentModalShown = () => {
      setIsMobileMenuOpen(false);
    };

    window.addEventListener('exit-intent-modal-shown', onExitIntentModalShown);

    return () => {
      window.removeEventListener(
        'exit-intent-modal-shown',
        onExitIntentModalShown
      );
    };
  }, []);

  // CatalogNavigation should always show on the catalog pages, regardless of exp enrollment. Using the requireExpEnrollment prop to bypass the exp logic on those pages.
  // This is because marketing wants to be able to share direct links to the new pages
  if (
    preAuthCatalogVariant === undefined ||
    (preAuthCatalogVariant < 1 && requireExpEnrollment)
  ) {
    return null;
  }

  return (
    <div className={className}>
      {desktopNavLinks}
      <TransparentButton
        testid="mobile-menu-open"
        ariaLabel="Open menu"
        className={styles['mobile-menu-trigger']}
        onClick={() => {
          setIsMobileMenuOpen(!isMobileMenuOpen);
          buttonClickedEvent({
            button_name: 'menu_header',
            order_id: null,
            page_position: '',
          });
        }}
      >
        <HamburgerMenuUtility className={styles.hamburger} />
      </TransparentButton>
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onDismiss={() => setIsMobileMenuOpen(false)}
        navLinks={mobileNavLinks}
      />
    </div>
  );
}
